<script setup lang="ts">
import Dialog from '@uiKit/elements/Dialog.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

interface Props {
  /** Controls dialog visibility. v-model compatible. */
  visible: boolean
  /** Human-readable label for the entity being approved (e.g. `Property`). */
  entityType?: string
  /** Show a pending state on the confirm button; disables close. */
  loading?: boolean
  /** Override the dialog title. Defaults to `Approve {entityType}`. */
  title?: string
  /** Override the body copy. Defaults to a generic approval message. */
  message?: string
  /** Override the confirm-button label. Defaults to `Confirm Approval`. */
  confirmLabel?: string
  /** Override the dialog width. Defaults to `560px`. */
  width?: string
  /** Override the dialog max-height. Defaults to `none`. */
  maxHeight?: string
  /** Disable the confirm button. */
  disableConfirm?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  entityType: 'Item',
  loading: false,
  width: '560px',
  maxHeight: 'none',
  disableConfirm: false,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm'): void
}>()

const handleConfirm = () => emit('confirm')
const handleClose = () => emit('update:visible', false)
</script>

<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :width="width"
    :max-height="maxHeight"
    :closable="!loading"
    @update:visible="handleClose"
  >
    <template #header>
      <div class="ad-header">
        <span class="ad-header__icon">
          <PiIcon name="check-circle" />
        </span>
        <span class="ad-header__title">{{ title || `Approve ${props.entityType}` }}</span>
      </div>
    </template>

    <div class="ad-body">
      <div class="ad-banner">
        <PiIcon name="check-circle" class="ad-banner__icon" />
        <div>
          <p class="ad-banner__title">{{ title ? 'Confirm Action' : 'Confirm Approval' }}</p>
          <p class="ad-banner__body">
            {{
              message ||
              `You are about to approve this ${props.entityType.toLowerCase()}. This action will be logged in the audit trail.`
            }}
          </p>
        </div>
      </div>
      <slot></slot>
    </div>

    <template #footer>
      <div class="ad-footer">
        <Button
          label="Cancel"
          color="danger"
          variant="outline"
          :disabled="loading"
          @click="handleClose"
        />
        <Button
          :label="confirmLabel || 'Confirm Approval'"
          icon="pi pi-check"
          severity="success"
          :loading="loading"
          :disabled="disableConfirm || loading"
          @click="handleConfirm"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.ad-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ad-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #dcfce7;
  color: #16a34a;
  font-size: 1rem;
  flex-shrink: 0;
}

.ad-header__title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--p-text-color);
}

.ad-body {
  padding: 0.25rem 0 0.5rem;
}

.ad-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.ad-banner__icon {
  font-size: 1.125rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
  color: #16a34a;
}

.ad-banner__title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #15803d;
  margin: 0 0 0.25rem;
}

.ad-banner__body {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #166534;
  margin: 0;
}

.ad-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
  padding: 0.25rem 0;
}
</style>
